import { LEVEL } from 'src/shared/constants/constants'
import { Utils } from 'src/shared/utils/Utils'
import { Kap_Goal_Measure, Kap_Measure, Kap_Measure_Annual_Report } from 'src/@types/graphql'

const sortOrder = Object.keys(LEVEL).map((level) => level)

/**
 * Sorts KAP measures by level in custom specified order. The original list is mutated and sorted.
 * The custom order is: Intervention - Policy - Network - Communication
 *
 * @param list list of objects that somehow contain measures or if nested connection is not provided
 *             then the measure list itself.
 * @param nestedConnection field which contains the {@link Kap_Measure} object.
 * @returns {Array} modified list with KAP measures sorted.
 */
const sortKapMeasures = (list?: any[], nestedConnection?: string): any[] => {
  if (!list) {
    return []
  }

  return list.sort(function (m1, m2) {
    const m1Level = nestedConnection ? m1[nestedConnection].level : m1.level
    const m2Level = nestedConnection ? m2[nestedConnection].level : m2.level
    const m1SortNumber = nestedConnection ? m1[nestedConnection].sort_number : m1.sort_number
    const m2SortNumber = nestedConnection ? m2[nestedConnection].sort_number : m2.sort_number

    if (m1Level === m2Level) {
      return m1SortNumber - m2SortNumber
    } else {
      return sortOrder.indexOf(m1Level) - sortOrder.indexOf(m2Level)
    }
  })
}

/**
 * Merge a list of Kap_Goal_Measure[] and a list of Kap_Measure_Annual_Report[] into a
 * list of (Kap_Measure & Kap_Measure_Annual_Report)[]
 *
 * @param kapGoalMeasures list of {@link Kap_Goal_Measure} objects
 * @param kapMeasureAnnualReport list of {@link Kap_Measure_Annual_Report} objects
 * @returns list of ({@link Kap_Measure} & {@link Kap_Measure_Annual_Report}) objects
 */
const mergeKapGoalMeasuresWithKapMeasureAnnualReport = (
  kapGoalMeasures: Kap_Goal_Measure[],
  kapMeasureAnnualReport: Kap_Measure_Annual_Report[],
): (Kap_Measure & Kap_Measure_Annual_Report)[] => {
  const linkedKapMeasureIds = kapGoalMeasures.map((gm) => gm.kap_measure.id)
  const measureAnnualReport = kapMeasureAnnualReport.filter((kmar) =>
    linkedKapMeasureIds.some((id) => id === kmar.kap_measure_id),
  )
  const goalMeasures = kapGoalMeasures.map((g) => g.kap_measure)

  return Utils.mergeBy({ arr: goalMeasures, key: 'id' }, { arr: measureAnnualReport, key: 'kap_measure_id' })
}

export { sortKapMeasures, mergeKapGoalMeasuresWithKapMeasureAnnualReport }
